import {create} from 'zustand';

const SOURCES_UPLOAD_CONCURRENCY = 1;
const SESSION_FILE_REGISTRY = new Map();

function createQueueItemId() {
    return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function registerQueuedFile(file) {
    const key = `file-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    SESSION_FILE_REGISTRY.set(key, file);
    return key;
}

export function getQueuedFile(fileKey) {
    return SESSION_FILE_REGISTRY.get(fileKey) || null;
}

export function removeQueuedFile(fileKey) {
    SESSION_FILE_REGISTRY.delete(fileKey);
}

function nowIso() {
    return new Date().toISOString();
}

function createQueueItem(entry, targetFolderId, classificationLevel, distribution, compartment) {
    const fileKey = registerQueuedFile(entry.file);
    const folderPath = Array.isArray(entry.folderPath) ? entry.folderPath : [];

    return {
        id: createQueueItemId(),
        sourceKind: 'file',
        fileKey,
        name: entry.file?.name || 'Untitled',
        size: Number(entry.file?.size || 0),
        mimeType: entry.file?.type || null,
        folderPath,
        targetFolderId: targetFolderId || null,
        classificationLevel: classificationLevel || null,
        distribution: Array.isArray(distribution) ? distribution : [],
        compartment: Array.isArray(compartment) ? compartment : [],
        status: 'queued',
        progress: 0,
        error: null,
        uploadedFileId: null,
        startedAt: null,
        finishedAt: null,
        createdAt: nowIso(),
    };
}

function deriveWebImportName(entry) {
    if (entry?.name && String(entry.name).trim()) {
        return String(entry.name).trim();
    }
    if (entry?.sourceKind === 'url' && entry?.url) {
        try {
            const parsed = new URL(String(entry.url).trim());
            const path = parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : '';
            return `${parsed.hostname}${path}`.replace(/\/$/, '') || parsed.hostname;
        } catch (_) {
            return String(entry.url).trim();
        }
    }
    return 'Pasted content';
}

// Web imports (URL / paste) don't carry a File blob; their payload travels on the
// queue item itself and is sent to the backend by the queue processor.
function createWebImportQueueItem(entry, targetFolderId) {
    return {
        id: createQueueItemId(),
        sourceKind: entry.sourceKind === 'paste' ? 'paste' : 'url',
        fileKey: null,
        name: deriveWebImportName(entry),
        size: 0,
        mimeType: entry.sourceKind === 'paste' ? null : 'html',
        folderPath: Array.isArray(entry.folderPath) ? entry.folderPath : [],
        targetFolderId: targetFolderId || null,
        classificationLevel: entry.classificationLevel || null,
        distribution: Array.isArray(entry.distribution) ? entry.distribution : [],
        compartment: Array.isArray(entry.compartment) ? entry.compartment : [],
        url: entry.url || null,
        htmlContent: entry.htmlContent || null,
        plainText: entry.plainText || null,
        status: 'queued',
        progress: 0,
        error: null,
        uploadedFileId: null,
        startedAt: null,
        finishedAt: null,
        createdAt: nowIso(),
    };
}

const useSourcesUploadQueueStore = create(
    (set, get) => ({
        items: [],
        paused: false,
        concurrency: SOURCES_UPLOAD_CONCURRENCY,
        // Default classification level applied to files enqueued via enqueueUploads (file picker +
        // drag-and-drop share this single default; see SourcesSectionContent's classification picker).
        classificationLevel: 'U',
        setClassificationLevel: (level) => set({classificationLevel: level || 'U'}),
        // Always ready — no localStorage rehydration needed.
        hydrated: true,
        enqueueUploads: (entries, options = {}) => {
            const targetFolderId = options.targetFolderId || null;
            const classificationLevel = options.classificationLevel || get().classificationLevel || null;
            const distribution = Array.isArray(options.distribution) ? options.distribution : [];
            const compartment = Array.isArray(options.compartment) ? options.compartment : [];
            const nextItems = entries.map((entry) => (
                createQueueItem(entry, targetFolderId, classificationLevel, distribution, compartment)
            ));
            set((state) => ({items: [...state.items, ...nextItems]}));
            return nextItems.map((item) => item.id);
        },
        enqueueWebImports: (entries, options = {}) => {
            const targetFolderId = options.targetFolderId || null;
            const nextItems = entries.map((entry) => createWebImportQueueItem(entry, targetFolderId));
            set((state) => ({items: [...state.items, ...nextItems]}));
            return nextItems.map((item) => item.id);
        },
        // Kept for API compatibility with any callers that still reference it; no-op.
        setHydrated: () => {},
        /**
         * Hydrate queue state from server.
         *
         * Called once on mount after GET /api/data-items/upload/queue resolves.
         *
         * Rules:
         *  1. For existing queue items whose uploadedFileId matches a server item →
         *     update status/stage/progressPercent from server (server wins; skip terminal items).
         *  2. For server items not yet in the local queue (new tab / new session) →
         *     inject as synthetic queue items so the user sees their in-progress uploads.
         */
        hydrateFromServer: (serverItems) => {
            if (!serverItems || !serverItems.length) {
                return;
            }

            const TERMINAL = new Set(['success', 'cancelled', 'duplicate']);

            const deriveLocalStatus = (serverStatus) => {
                if (serverStatus === 'processed') return 'success';
                if (serverStatus === 'failed') return 'failed';
                return 'processing'; // pending | processing → pipeline still running (not an XHR transfer)
            };

            set((state) => {
                // Match by canonicalItemId (the canonical FILE id returned by the upload endpoint
                // as referencedDataItemId). uploadedFileId holds the REFERENCE row id which is a
                // different value — using it to match against the FILE-keyed server list caused
                // duplicate entries when the file was still processing.
                const existingByCanonicalId = new Map(
                    state.items
                        .filter((i) => i.canonicalItemId)
                        .map((i) => [String(i.canonicalItemId), i]),
                );

                // 1. Update status for items that already have a matching canonical id
                const updated = state.items.map((item) => {
                    if (!item.canonicalItemId) return item;
                    const server = serverItems.find((s) => String(s.id) === String(item.canonicalItemId));
                    if (!server) return item;
                    if (TERMINAL.has(item.status)) return item;
                    return {
                        ...item,
                        status: deriveLocalStatus(server.status),
                        stage: server.stage || null,
                        progressPercent: server.progressPercent ?? null,
                    };
                });

                // 2. Inject server items not already in the local queue (new tab / new session)
                const injected = serverItems
                    .filter((s) => !existingByCanonicalId.has(String(s.id)))
                    .map((s) => ({
                        id: `server-${s.id}-${Math.random().toString(36).slice(2, 8)}`,
                        sourceKind: 'file',
                        fileKey: null,
                        name: s.name || 'Untitled',
                        size: s.size || 0,
                        mimeType: s.mimeType || null,
                        folderPath: [],
                        targetFolderId: null,
                        status: deriveLocalStatus(s.status),
                        progress: s.progressPercent != null ? s.progressPercent : 0,
                        stage: s.stage || null,
                        progressPercent: s.progressPercent ?? null,
                        error: null,
                        uploadedFileId: null,
                        canonicalItemId: String(s.id),
                        existingReferenceId: null,
                        startedAt: null,
                        finishedAt: null,
                        createdAt: s.createdAt || nowIso(),
                    }));

                return {items: [...updated, ...injected]};
            });
        },
        markUploading: (itemId) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {
                            ...item,
                            status: 'uploading',
                            startedAt: item.startedAt || nowIso(),
                            error: null,
                        }
                        : item
                )),
            }));
        },
        markProgress: (itemId, progress) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {...item, progress: Math.max(item.progress || 0, progress)}
                        : item
                )),
            }));
        },
        markSuccess: (itemId, payload = {}) => {
            // serverStatus comes from the upload acceptance response.
            // 'processed' means the pipeline finished immediately (rare — deduplication/instant).
            // Any other value (pending, processing, null) means the pipeline is still running;
            // set local status to 'processing' so the polling loop can track it to completion.
            const pipelineDone = payload.serverStatus === 'processed';
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {
                            ...item,
                            status: pipelineDone ? 'success' : 'processing',
                            progress: 100,
                            error: null,
                            uploadedFileId: payload.uploadedFileId || null,
                            canonicalItemId: payload.canonicalItemId || null,
                            stage: payload.stage || null,
                            progressPercent: payload.progressPercent ?? null,
                            finishedAt: pipelineDone ? nowIso() : null,
                        }
                        : item
                )),
            }));
        },
        // Called by the polling loop when the server confirms the pipeline is done.
        markPipelineDone: (canonicalItemId) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.canonicalItemId === String(canonicalItemId)
                        ? {...item, status: 'success', finishedAt: nowIso(), stage: null, progressPercent: 100}
                        : item
                )),
            }));
        },
        markFailed: (itemId, message) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {
                            ...item,
                            status: 'failed',
                            error: message || 'Upload failed',
                            finishedAt: nowIso(),
                        }
                        : item
                )),
            }));
        },
        markDuplicate: (itemId, message, existingReferenceId = null) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {
                            ...item,
                            status: 'duplicate',
                            progress: 100,
                            error: message || 'This file is already in My files.',
                            existingReferenceId: existingReferenceId ?? null,
                            finishedAt: nowIso(),
                        }
                        : item
                )),
            }));
        },
        markCancelled: (itemId, message = 'Canceled') => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {
                            ...item,
                            status: 'cancelled',
                            error: message,
                            finishedAt: nowIso(),
                        }
                        : item
                )),
            }));
        },
        markPaused: (itemId) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {
                            ...item,
                            status: 'paused',
                            error: 'Paused',
                        }
                        : item
                )),
            }));
        },
        retryItem: (itemId) => {
            set((state) => ({
                items: state.items.map((item) => (
                    item.id === itemId
                        ? {
                            ...item,
                            status: 'queued',
                            progress: 0,
                            error: null,
                            uploadedFileId: null,
                            finishedAt: null,
                        }
                        : item
                )),
            }));
        },
        pauseAll: () => set({paused: true}),
        resumeAll: () => {
            set((state) => ({
                paused: false,
                items: state.items.map((item) => (
                    item.status === 'paused'
                        ? {...item, status: 'queued', error: null}
                        : item
                )),
            }));
        },
        setPaused: (paused) => set({paused}),
        clearCompleted: () => {
            const items = get().items;
            items.forEach((item) => {
                if (item.status === 'success' || item.status === 'cancelled' || item.status === 'duplicate') {
                    removeQueuedFile(item.fileKey);
                }
            });

            set((state) => ({
                items: state.items.filter((item) => (
                    item.status !== 'success' && item.status !== 'cancelled' && item.status !== 'duplicate'
                )),
            }));
        },
        clearAll: () => {
            const items = get().items;
            items.forEach((item) => removeQueuedFile(item.fileKey));
            set({items: []});
        },
        removeItem: (itemId) => {
            const item = get().items.find((candidate) => candidate.id === itemId);
            if (item) {
                removeQueuedFile(item.fileKey);
            }

            set((state) => ({items: state.items.filter((candidate) => candidate.id !== itemId)}));
        },
    })
);

export default useSourcesUploadQueueStore;
